import { IdentityReservedHashParams } from '../constants';
import type { RedirectHashParams } from '../types/hashParams';
import type { Response } from '../types/response';
import type { MutationError } from '../types/mutations';
import type { ErrorResponseType } from '../components/useErrorResponses';

import { urlHash } from './urlHash';

export const obfuscateMobileNumber = (mobileNumber: string) => {
  if (!mobileNumber) {
    return '-';
  }
  return mobileNumber.replace(/(\d{2})(\d{5})(\d{3})/, '$1** *** $3');
};

export const createHashParams = (params: RedirectHashParams) => {
  return (Object.keys(params) as Array<keyof RedirectHashParams>)
    .map((hashKey) => {
      return hashKey.concat('=', params[hashKey]!);
    })
    .join('&');
};

function extractURLParams(path: string) {
  return new URLSearchParams(path.split('?')[1]);
}

export function extractRedirectURLFromPath(path: string) {
  const urlParams = extractURLParams(path);

  const redirectURL = urlParams.get('redirect_url') || '';
  const filteredRedirectUrl = removeIdentitySpecificKeysFromRedirectURLHash(
    redirectURL
  );

  return {
    redirectURL: filteredRedirectUrl,
  };
}

export function removeIdentitySpecificKeysFromRedirectURLHash(
  redirectURL: string
) {
  // hack for portal #callback hash route
  if (redirectURL.includes('#callback')) {
    return redirectURL;
  }
  const [redirectUrl, redirectURLHash] = redirectURL.split('#');

  if (!redirectURLHash) {
    return redirectURL;
  }
  const filteredHash = urlHash<Record<string, string>>(redirectURLHash).remove(
    IdentityReservedHashParams
  );
  try {
    const url = new URL(redirectUrl);
    url.hash = filteredHash.toString();
    return url.toString();
  } catch (e) {
    // do nothing as console.log not allowed. Error will be caught and return 404 in getClientData
    throw e;
  }
}

export function extractAccessTokenFromPath(path: string) {
  const urlParams = extractURLParams(path);

  const accessToken = urlParams.get('accessToken') || '';

  return {
    accessToken,
  };
}

export function isError(obj: any): obj is Response {
  return obj && obj.result === 'error';
}

export function getErrorMessageFromErrorType(
  errorType: string,
  errorMessage: string
): { message: string; statusType: 'reset_password' | 'register' | 'none' } {
  if (errorType === 'Not Found') {
    return {
      message: "The username or password you've entered is incorrect",
      statusType: 'register',
    };
  }

  if (
    errorType === 'Unauthorized' &&
    errorMessage === 'Unable to validate user'
  ) {
    return {
      message: "The username or password you've entered is incorrect",
      statusType: 'none',
    };
  }

  if (errorType === 'Unauthorized' && errorMessage === 'User not verified') {
    return {
      message:
        'This email address has not been verified, check your inbox for the verification email. Reset your password if you have lost the verification email.',
      statusType: 'none',
    };
  }

  if (
    errorType === 'Unauthorized' &&
    errorMessage.includes('login attempt/s remaining.')
  ) {
    return {
      message: "The username or password you've entered is incorrect",
      statusType: 'reset_password',
    };
  }

  if (errorType === 'Unauthorized' && errorMessage === 'User is locked') {
    return {
      message: "You've been locked out for too many failed login attempts.",
      statusType: 'reset_password',
    };
  }

  return {
    message: 'Something went wrong. Please try again later.',
    statusType: 'none',
  };
}

export function transformError(
  error: MutationError
): ErrorResponseType | undefined {
  if (error) {
    return {
      tone: 'critical',
      description: error.message || 'Something went wrong',
    };
  } else {
    return undefined;
  }
}
